import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { showToast } from "../../../../utils/toast";
import {
  getPayoutBalanceAPI,
  getMyPayoutsAPI,
  requestPayoutAPI,
} from "../../../../api/payoutsApi";
import {
  myTaxFormsAPI,
  submitTaxFormAPI,
  uploadTaxFormAPI,
  generateTaxFormPdfAPI,
  saveTaxFormSignatureAPI,
  updateTaxFormDraftAPI,
  startTaxFormDraftAPI,
  myRequiredTaxFormAPI,
} from "../../../../api/taxFormsApi";
import { getFileByIdAPI } from "../../../../api/userApi";

// -------- Types --------
type PayoutStatus =
  | "pending"
  | "approved"
  | "processing"
  | "paid"
  | "rejected"
  | "canceled";
type ChipColor = "default" | "primary" | "secondary";

interface IBalance {
  earnedAvailable: number;
  reserved: number;
  paid: number;
  available: number;
  settlementDays: number;
}

interface IPayoutItem {
  _id: string;
  creatorId: string;
  amount: number;
  currency?: string;
  status: PayoutStatus;
  method?: string;
  note?: string;
  providerRef?: string;
  paidAt?: string;
  createdAt?: string;
}

interface IPayoutListRes {
  items: IPayoutItem[];
  total: number;
  page: number;
  limit: number;
}

type PayoutMethod = "bank" | "paypal" | "wise" | "manual";
type TaxType = "W9" | "W8BEN";

const useStyles = makeStyles(theme => ({
  container: { padding: 24 },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  balanceCard: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(140px, 1fr))",
    gap: 16,
    marginTop: 16,
    background: "#141414",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 16,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "none",
    },
  },
  statBox: {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 16,
    background: "#0E0E0E",
  },
  statLabel: { color: "#FFFFFF", fontSize: 14 },
  statValue: { fontSize: 22, fontWeight: 700, marginTop: 6, color: "#FFFFFF" },
  actions: { display: "flex", gap: 12 },
  tableWrap: {
    marginTop: 24,
    background: "#141414",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  tableHeaderCell: { color: "#FFFFFF", fontWeight: 600 },
  statusChip: { textTransform: "capitalize" },
  tdHelp: { color: "#FFFFFF" },
  pagination: { color: "#FFFFFF" },
  // inputs in dialogs
  whiteInput: { color: "#fff" },
  whiteLabel: { color: "#fff" },
  taxTab: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    padding: "6px 12px",
    borderWidth: 1,
    borderStyle: "solid",
    transition: "background-color .15s ease, border-color .15s ease",
  },
  taxTabActive: {
    backgroundColor: "#F472B6", // рожевий
    color: "#fff",
    borderColor: "#F472B6",
    "&:hover": { backgroundColor: "#F472B6", borderColor: "#F472B6" },
  },
  taxTabInactive: {
    backgroundColor: "#0B0B0F", // чорний
    color: "#fff",
    borderColor: "rgba(255,255,255,0.2)",
    "&:hover": {
      backgroundColor: "#111",
      borderColor: "rgba(255,255,255,0.35)",
    },
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    "& .MuiLinearProgress-bar": {
      borderRadius: 4,
    },
  },
  progressBarReady: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#4CAF50",
    },
  },
  progressBarInProgress: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#FE5A97",
    },
  },
}));
// зверху файлу (разом з іншими константами)
const LOCAL_W9_URL = "/forms/fw9.pdf";
const LOCAL_W8BEN_URL = "/forms/fw8ben.pdf";
const getBlankFormUrl = (t: "W9" | "W8BEN") =>
  t === "W9" ? LOCAL_W9_URL : LOCAL_W8BEN_URL;

const statusColor = (s: PayoutStatus): ChipColor => {
  switch (s) {
    case "approved":
    case "paid":
      return "primary";
    case "processing":
    case "rejected":
      return "secondary";
    default:
      return "default";
  }
};

const currency = (n: number | undefined) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleString() : "—");
const getErrMsg = (e: any) =>
  e?.message ||
  e?.data?.message ||
  e?.response?.data?.message ||
  "Something went wrong";

const MIN_PAYOUT = 50; // Minimum payout threshold: $50 USD
const isValidEmail = (s: string) => /\S+@\S+\.\S+/.test(s);

const PayoutsPage: React.FC = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [balance, setBalance] = useState<IBalance>({
    earnedAvailable: 0,
    reserved: 0,
    paid: 0,
    available: 0,
    settlementDays: 7,
  });
  const [listLoading, setListLoading] = useState<boolean>(true);
  const [items, setItems] = useState<IPayoutItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [payoutTab, setPayoutTab] = useState<"all" | "pending" | "processed">(
    "all"
  );
  const [openReq, setOpenReq] = useState<boolean>(false);
  const [amount, setAmount] = useState<string>("");
  const [method, setMethod] = useState<PayoutMethod>("bank");
  const [destination, setDestination] = useState<{
    iban: string;
    email: string;
  }>({ iban: "", email: "" });
  const [note, setNote] = useState<string>("");
  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const [openTax, setOpenTax] = useState<boolean>(false);
  const [taxType, setTaxType] = useState<TaxType>("W9");
  const [taxUploading, setTaxUploading] = useState<boolean>(false);
  const [taxFile, setTaxFile] = useState<File | null>(null);
  const [lastTaxStatus, setLastTaxStatus] = useState<string>(""); // pending/verified/rejected/''
  const [lastTaxFile] = useState<{
    fileId?: string;
    mediaUrl?: string;
    filename?: string;
  } | null>(null);

  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>(""); // для прев’ю (wizard)

  const [taxTab, setTaxTab] = useState<"upload" | "wizard">("upload");
  const [wizardStep, setWizardStep] = useState<number>(0); // 0 Type → 1 Fields → 2 Signature → 3 Generate → 4 Submit
  const [draft, setDraft] = useState<any>(null); // { _id, type, fields, signature... }
  const [draftLoading, setDraftLoading] = useState<boolean>(false);
  const [fields, setFields] = useState<any>({
    name: "",
    businessName: "",
    taxClass: "individual",
    tin: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });
  const [sigMethod] = useState<"typed" | "draw">("typed");
  const [sigTypedName, setSigTypedName] = useState<string>("");
  const [sigAgree, setSigAgree] = useState<boolean>(false);
  const [pdfGenerating, setPdfGenerating] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  // const loadMyTaxFormss = async () => {
  //     try {
  //         const data: any = await myTaxFormsAPI();
  //         const last = data?.items?.[0]; // припускаємо, що 0-й — найсвіжіший
  //         if (last?.status) setLastTaxStatus(last.status); else setLastTaxStatus("");
  //
  //         // нормалізуємо file id / url з того, що повертає бек
  //         const mediaUrl: string | undefined =
  //             last?.mediaUrl ||
  //             last?.file?.mediaUrl ||
  //             (last?.fileId ? `/api/files/${last.fileId}` : undefined);
  //
  //         const fileId =
  //             last?.fileId ||
  //             (typeof mediaUrl === "string" && mediaUrl.startsWith("/api/files/")
  //                 ? mediaUrl.split("/api/files/")[1]
  //                 : undefined);
  //
  //         setLastTaxFile(
  //             mediaUrl || fileId
  //                 ? { mediaUrl, fileId, filename: last?.filename || `tax-form-${(last?.type || "w9").toLowerCase()}.pdf` }
  //                 : null
  //         );
  //     } catch {
  //         setLastTaxStatus("");
  //         setLastTaxFile(null);
  //     }
  // };
  // Відкрити посилання у новій вкладці
  const openInNewTab = (url: string) =>
    window.open(url, "_blank", "noopener,noreferrer");

  // Скачати файл з /api/files/:id через getFileByIdAPI (бо потрібні токени)
  const downloadApiFileById = async (
    fileId: string,
    filename = "document.pdf"
  ) => {
    try {
      const blobUrl = await getFileByIdAPI(fileId); // повертає blob:...
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // обов’язково звільнимо URL трохи пізніше
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (e) {
      showToast({ message: "Failed to download file", err: true });
      console.error(e);
    }
  };

  // Маленький компонент прев’ю PDF (iframe/object)
  const PdfPreview: React.FC<{ url?: string }> = ({ url }) => {
    if (!url) return null;
    return (
      <Box
        style={{
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8,
          overflow: "hidden",
          background: "#000",
        }}
      >
        <object data={url} type="application/pdf" width="100%" height="500">
          <iframe
            src={`${url}#toolbar=0`}
            width="100%"
            height="500"
            title="PDF preview"
          />
        </object>
      </Box>
    );
  };
  useEffect(() => {
    let revokeTimer: any;
    const run = async () => {
      // якщо pdfUrl пустий — показуємо бланк, а прев’ю обнуляємо
      if (!pdfUrl) {
        setPdfBlobUrl("");
        return;
      }
      // якщо це вже blob/http(s) — можна використати напряму
      if (/^blob:|^https?:\/\//i.test(pdfUrl)) {
        setPdfBlobUrl(pdfUrl);
        return;
      }
      // якщо це /api/files/:id — беремо fileId і тягнемо blob через getFileByIdAPI
      if (pdfUrl.startsWith("/api/files/")) {
        const fileId = pdfUrl.split("/api/files/")[1];
        if (fileId) {
          const blobUrl = await getFileByIdAPI(fileId);
          setPdfBlobUrl(blobUrl);
          // при оновленні/розмонтуванні — звільнимо URL
          revokeTimer = setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        }
      } else {
        // якийсь інший локальний шлях — віддамо як є
        setPdfBlobUrl(pdfUrl);
      }
    };
    run();
    return () => {
      if (revokeTimer) clearTimeout(revokeTimer);
    };
  }, [pdfUrl]);

  const canRequest = useMemo(() => {
    const a = Number(amount);
    if (!Number.isFinite(a)) return false;
    if (a < MIN_PAYOUT) return false;
    if (a > Number(balance?.available || 0)) return false;

    if (method === "bank") return !!destination.iban.trim();
    if (method === "paypal" || method === "wise")
      return isValidEmail(destination.email);
    return true; // manual
  }, [amount, balance, method, destination]);

  const loadBalance = async () => {
    setLoading(true);
    try {
      const data = (await getPayoutBalanceAPI()) as IBalance;
      setBalance(data || ({} as IBalance));
    } catch (e) {
      console.error(e);
      showToast({ message: "Failed to load balance", err: true });
    } finally {
      setLoading(false);
    }
  };

  const loadList = async (pageArg = page, limitArg = limit) => {
    setListLoading(true);
    try {
      const res = (await getMyPayoutsAPI({
        page: pageArg + 1,
        limit: limitArg,
      })) as IPayoutListRes;
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
      showToast({ message: "Failed to load payouts", err: true });
    } finally {
      setListLoading(false);
    }
  };

  const loadMyTaxForms = async () => {
    try {
      const data: any = await myTaxFormsAPI();
      const last = data?.items?.[0];
      if (last?.status)
        setLastTaxStatus(last.status); // pending | verified | rejected
      else setLastTaxStatus("");
    } catch {
      setLastTaxStatus("");
    }
  };

  useEffect(() => {
    loadBalance();
    loadList(0, limit);
    loadMyTaxForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter payouts based on active tab
  const filteredItems = useMemo(() => {
    if (payoutTab === "all") return items;
    if (payoutTab === "pending") {
      return items.filter(
        item =>
          item.status === "pending" ||
          item.status === "processing" ||
          item.status === "approved"
      );
    }
    if (payoutTab === "processed") {
      return items.filter(
        item => item.status === "paid" || item.status === "rejected"
      );
    }
    return items;
  }, [items, payoutTab]);

  const onAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const n = Math.max(
      0,
      Math.min(Number(raw), Number(balance?.available || 0))
    );
    if (Number.isFinite(n)) setAmount(n.toFixed(2));
    else setAmount(raw);
  };

  const openRequestDialog = () => {
    if (lastTaxStatus !== "verified") {
      showToast({
        message: "Please upload and verify your tax form first.",
        err: true,
      });
      setOpenTax(true);
      return;
    }
    setAmount(String(Number(balance?.available || 0).toFixed(2)));
    setMethod("bank");
    setDestination({ iban: "", email: "" });
    setNote("");
    setOpenReq(true);
  };

  const submitRequest = async () => {
    try {
      setRequestLoading(true);
      const dest =
        method === "bank"
          ? { iban: destination.iban }
          : method === "manual"
          ? {}
          : { email: destination.email };

      await requestPayoutAPI({
        amount: Number(amount),
        method,
        destination: dest,
        note,
      });
      showToast({ message: "Payout request submitted", success: true });
      setOpenReq(false);
      await Promise.all([loadBalance(), loadList()]);
    } catch (e) {
      const msg = getErrMsg(e);
      if (/tax form/i.test(msg) || /w-9|w9|w-8ben|w8ben/i.test(msg)) {
        showToast({
          message: "Tax form required. Please upload your W-9 / W-8BEN.",
          err: true,
        });
        setOpenTax(true);
      } else {
        showToast({ message: msg, err: true });
      }
    } finally {
      setRequestLoading(false);
    }
  };

  const submitTaxForm = async () => {
    if (!taxFile) {
      showToast({ message: "Please choose a file (PDF/JPG/PNG)", err: true });
      return;
    }
    setTaxUploading(true);
    try {
      await uploadTaxFormAPI({ type: taxType, file: taxFile });
      showToast({
        message: "Tax form uploaded. Status: pending review.",
        success: true,
      });
      setTaxFile(null);
      setOpenTax(false);
      loadMyTaxForms();
    } catch (e) {
      showToast({ message: getErrMsg(e), err: true });
    } finally {
      setTaxUploading(false);
    }
  };

  const beginWizard = async () => {
    setTaxTab("wizard");
    setWizardStep(0);
    setPdfUrl("");
    setSigTypedName("");
    setSigAgree(false);
    setFields({
      name: "",
      businessName: "",
      taxClass: "individual",
      tin: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    });

    // спробуємо підказати потрібний тип
    try {
      const req: any = await myRequiredTaxFormAPI().catch(() => null);
      if (req?.requiredType === "W8BEN" || req?.requiredType === "W9") {
        setTaxType(req.requiredType);
      }
    } catch {}

    if (!openTax) setOpenTax(true);
  };

  const createDraft = async () => {
    setDraftLoading(true);
    try {
      const res: any = await startTaxFormDraftAPI({ type: taxType });
      setDraft(res?.draft || null);
      setWizardStep(1);
    } catch (e) {
      showToast({ message: "Failed to start draft", err: true });
    } finally {
      setDraftLoading(false);
    }
  };

  const saveFields = async () => {
    if (!draft?._id) return;
    setDraftLoading(true);
    try {
      const res: any = await updateTaxFormDraftAPI({ id: draft._id, fields });
      setDraft(res?.draft || draft);
      setWizardStep(2);
    } catch {
      showToast({ message: "Failed to save form fields", err: true });
    } finally {
      setDraftLoading(false);
    }
  };

  const saveSignature = async () => {
    if (!draft?._id) return;
    if (sigMethod === "typed" && (!sigTypedName || !sigAgree)) {
      showToast({
        message: "Enter your full name and accept certification",
        err: true,
      });
      return;
    }
    setDraftLoading(true);
    try {
      const payload =
        sigMethod === "typed"
          ? { method: "typed", name: sigTypedName }
          : { method: "draw", dataUrl: "" }; // намальоване додамо за бажанням
      const res: any = await saveTaxFormSignatureAPI({
        id: draft._id,
        payload,
      });
      setDraft(res?.draft || draft);
      setWizardStep(3);
    } catch {
      showToast({ message: "Failed to save signature", err: true });
    } finally {
      setDraftLoading(false);
    }
  };

  const generatePdf = async () => {
    if (!draft?._id) return;
    setPdfGenerating(true);
    try {
      const res: any = await generateTaxFormPdfAPI({ id: draft._id });
      const url = res?.file?.mediaUrl || res?.draft?.generatedPdfUrl || "";
      setPdfUrl(url);
      setWizardStep(4);
    } catch {
      showToast({ message: "Failed to generate PDF", err: true });
    } finally {
      setPdfGenerating(false);
    }
  };

  const submitWizard = async () => {
    if (!draft?._id) return;
    setSubmitting(true);
    try {
      await submitTaxFormAPI({ id: draft._id });
      showToast({
        message: "Tax form submitted. Status: pending review.",
        success: true,
      });
      setOpenTax(false);
      await loadMyTaxForms();
    } catch (e) {
      showToast({ message: getErrMsg(e), err: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <Typography style={{ color: "#FFFFFF" }} variant="h5">
          Payouts
        </Typography>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={openRequestDialog}
            disabled={loading || (balance?.available || 0) <= 0}
          >
            Request payout
          </Button>
          <Button
            variant="outlined"
            onClick={loadBalance}
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Tax form status banner */}
      {lastTaxStatus !== "verified" ? (
        <Box
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            background: "#2a1a1a",
            border: "1px solid #813131",
          }}
        >
          <Typography style={{ color: "#fff" }}>
            Tax form status: <b>{lastTaxStatus || "none"}</b>. Please upload
            your W-9 / W-8BEN to request payouts.
          </Typography>
          <Button
            onClick={() => setOpenTax(true)}
            variant="contained"
            color="primary"
            style={{ marginTop: 8 }}
          >
            {lastTaxStatus ? "Replace form" : "Upload form"}
          </Button>
        </Box>
      ) : (
        <Box
          style={{
            marginTop: 12,
            padding: 8,
            borderRadius: 8,
            background: "#142a14",
            border: "1px solid #2f6d2f",
          }}
        >
          <Typography style={{ color: "#cfe9cf" }}>
            ✅ Tax form on file (verified).
          </Typography>
        </Box>
      )}

      {/* Balance */}
      <Box className={classes.balanceCard}>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>
            Available (after hold)
          </Typography>
          <Typography className={classes.statValue}>
            {loading ? "…" : currency(balance?.available)}
          </Typography>
          <Typography style={{ color: "#FFFFFF", fontSize: 12, marginTop: 4 }}>
            Settlement: T+{balance?.settlementDays || 7} days • Min payout $
            {MIN_PAYOUT}
          </Typography>
          {/* Progress bar for minimum payout threshold */}
          {!loading && (
            <Box style={{ marginTop: 12 }}>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <Typography
                  style={{
                    color: "#FFFFFF",
                    fontSize: 11,
                    fontWeight: 500,
                  }}
                >
                  Progress to minimum payout
                </Typography>
                <Typography
                  style={{
                    color: "#FFFFFF",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {Math.min(
                    Math.round(((balance?.available || 0) / MIN_PAYOUT) * 100),
                    100
                  )}
                  %
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  ((balance?.available || 0) / MIN_PAYOUT) * 100,
                  100
                )}
                className={`${classes.progressBar} ${
                  (balance?.available || 0) >= MIN_PAYOUT
                    ? classes.progressBarReady
                    : classes.progressBarInProgress
                }`}
              />
              <Typography
                style={{
                  color: "#BBBBBB",
                  fontSize: 10,
                  marginTop: 4,
                  textAlign: "right",
                }}
              >
                {currency(balance?.available || 0)} / ${MIN_PAYOUT}
                {(balance?.available || 0) >= MIN_PAYOUT && (
                  <span style={{ color: "#4CAF50", marginLeft: 4 }}>
                    ✓ Ready
                  </span>
                )}
              </Typography>
            </Box>
          )}
        </Box>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>
            Reserved (in requests)
          </Typography>
          <Typography className={classes.statValue}>
            {loading ? "…" : currency(balance?.reserved)}
          </Typography>
        </Box>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>Paid total</Typography>
          <Typography className={classes.statValue}>
            {loading ? "…" : currency(balance?.paid)}
          </Typography>
        </Box>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>
            Earned (eligible)
          </Typography>
          <Typography className={classes.statValue}>
            {loading ? "…" : currency(balance?.earnedAvailable)}
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <Box className={classes.tableWrap}>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
          }}
        >
          <Typography style={{ color: "#FFFFFF" }} variant="subtitle1">
            My payout requests
          </Typography>
          {listLoading && <CircularProgress size={20} />}
        </Box>
        <Divider />
        {/* Tabs for filtering payouts */}
        <Box style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <Tabs
            value={payoutTab}
            onChange={(_, newValue) => setPayoutTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            style={{
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Tab
              label={`All (${items.length})`}
              value="all"
              style={{
                color: payoutTab === "all" ? "#FE5A97" : "#FFFFFF",
                textTransform: "none",
                fontWeight: payoutTab === "all" ? 600 : 400,
              }}
            />
            <Tab
              label={`Pending (${
                items.filter(
                  item =>
                    item.status === "pending" ||
                    item.status === "processing" ||
                    item.status === "approved"
                ).length
              })`}
              value="pending"
              style={{
                color: payoutTab === "pending" ? "#FE5A97" : "#FFFFFF",
                textTransform: "none",
                fontWeight: payoutTab === "pending" ? 600 : 400,
              }}
            />
            <Tab
              label={`Processed (${
                items.filter(
                  item => item.status === "paid" || item.status === "rejected"
                ).length
              })`}
              value="processed"
              style={{
                color: payoutTab === "processed" ? "#FE5A97" : "#FFFFFF",
                textTransform: "none",
                fontWeight: payoutTab === "processed" ? 600 : 400,
              }}
            />
          </Tabs>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}>Created</TableCell>
              <TableCell className={classes.tableHeaderCell}>Amount</TableCell>
              <TableCell className={classes.tableHeaderCell}>Status</TableCell>
              <TableCell className={classes.tableHeaderCell}>Method</TableCell>
              <TableCell className={classes.tableHeaderCell}>Paid at</TableCell>
              <TableCell className={classes.tableHeaderCell}>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map(row => (
              <TableRow key={row._id}>
                <TableCell className={classes.tdHelp}>
                  {fmtDate(row.createdAt)}
                </TableCell>
                <TableCell className={classes.tdHelp}>
                  {currency(row.amount)} {row.currency || "USD"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={statusColor(row.status)}
                    className={classes.statusChip}
                  />
                </TableCell>
                <TableCell className={classes.tdHelp}>
                  {row.method || "—"}
                </TableCell>
                <TableCell className={classes.tdHelp}>
                  {fmtDate(row.paidAt)}
                </TableCell>
                <TableCell className={classes.tdHelp}>
                  {row.note || "—"}
                </TableCell>
              </TableRow>
            ))}
            {!listLoading && filteredItems.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  style={{ textAlign: "center", padding: 24, color: "#FFFFFF" }}
                >
                  {payoutTab === "pending"
                    ? "No pending payout requests"
                    : payoutTab === "processed"
                    ? "No processed payout requests"
                    : "No payout requests yet"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={payoutTab === "all" ? total : filteredItems.length}
          page={payoutTab === "all" ? page : 0}
          className={classes.pagination}
          onPageChange={(_e, p) => {
            if (payoutTab === "all") {
              setPage(p);
              loadList(p, limit);
            }
          }}
          rowsPerPage={payoutTab === "all" ? limit : filteredItems.length}
          onRowsPerPageChange={e => {
            if (payoutTab === "all") {
              const v = parseInt((e.target as HTMLInputElement).value, 10);
              setLimit(v);
              setPage(0);
              loadList(0, v);
            }
          }}
          rowsPerPageOptions={payoutTab === "all" ? [10, 20, 50] : []}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            payoutTab === "all"
              ? `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              : `${filteredItems.length} ${
                  filteredItems.length === 1 ? "item" : "items"
                }`
          }
        />
      </Box>

      {/* Request Payout Dialog */}
      <Dialog
        open={openReq}
        onClose={() => setOpenReq(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: "#fff" }}>Request payout</DialogTitle>
        <DialogContent dividers style={{ color: "#fff" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Amount, $"
                value={amount}
                onChange={onAmountChange}
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                helperText={`Min ${MIN_PAYOUT}, Max ${currency(
                  balance?.available
                )}`}
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{ style: { color: "#fff" }, shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Method"
                value={method}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMethod(e.target.value as PayoutMethod)
                }
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{ style: { color: "#fff" }, shrink: true }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { background: "#121212", color: "#fff" },
                    },
                  },
                }}
              >
                <MenuItem style={{ color: "#fff" }} value="bank">
                  Bank transfer
                </MenuItem>
                <MenuItem style={{ color: "#fff" }} value="paypal">
                  PayPal
                </MenuItem>
                <MenuItem style={{ color: "#fff" }} value="wise">
                  Wise
                </MenuItem>
                <MenuItem style={{ color: "#fff" }} value="manual">
                  Manual
                </MenuItem>
              </TextField>
            </Grid>

            {method === "bank" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="IBAN / Account"
                  value={destination.iban}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDestination({ ...destination, iban: e.target.value })
                  }
                  InputProps={{ style: { color: "#fff" } }}
                  InputLabelProps={{ style: { color: "#fff" }, shrink: true }}
                />
              </Grid>
            )}
            {(method === "paypal" || method === "wise") && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Payout Email"
                  value={destination.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDestination({ ...destination, email: e.target.value })
                  }
                  InputProps={{ style: { color: "#fff" } }}
                  InputLabelProps={{ style: { color: "#fff" }, shrink: true }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Note (optional)"
                value={note}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNote(e.target.value)
                }
                multiline
                rows={3}
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{ style: { color: "#fff" }, shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            style={{ color: "white" }}
            onClick={() => setOpenReq(false)}
            disabled={requestLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={submitRequest}
            disabled={!canRequest || requestLoading}
          >
            {requestLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tax Form Dialog */}
      {/* Tax Form Dialog */}
      <Dialog
        open={openTax}
        onClose={() => setOpenTax(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: "#fff" }}>Tax form</DialogTitle>

        <Box px={2} pt={1} display="flex">
          <Button
            size="small"
            variant="outlined"
            className={`${classes.taxTab} ${
              taxTab === "upload"
                ? classes.taxTabActive
                : classes.taxTabInactive
            }`}
            onClick={() => setTaxTab("upload")}
          >
            Upload file (PDF/JPG/PNG)
          </Button>

          <Button
            size="small"
            variant="outlined"
            className={`${classes.taxTab} ${
              taxTab === "wizard"
                ? classes.taxTabActive
                : classes.taxTabInactive
            }`}
            onClick={beginWizard}
          >
            Fill online (recommended)
          </Button>
        </Box>

        <DialogContent dividers style={{ color: "#fff" }}>
          {taxTab === "upload" ? (
            <>
              <Typography style={{ color: "#fff", marginBottom: 12 }}>
                Please upload your tax form (W-9 for U.S. persons, W-8BEN for
                non-U.S.). Formats: PDF / JPG / PNG.
              </Typography>
              {lastTaxStatus && (
                <Typography
                  style={{ color: "#fff", opacity: 0.8, marginBottom: 12 }}
                >
                  Last submission status: <b>{lastTaxStatus}</b>
                </Typography>
              )}

              <Grid container spacing={2}>
                {/*<Grid item xs={12}>*/}
                {/*    <TextField*/}
                {/*        select fullWidth variant="outlined" label="Form type"*/}
                {/*        value={taxType}*/}
                {/*        onChange={(e: React.ChangeEvent<any>) => setTaxType(e.target.value as TaxType)}*/}
                {/*        InputProps={{ style: { color: "#fff" } }}*/}
                {/*        InputLabelProps={{ style: { color: "#fff" }, shrink: true }}*/}
                {/*        SelectProps={{ MenuProps: { PaperProps: { style: { background: "#121212", color: "#fff" } } } }}*/}
                {/*    >*/}
                {/*        <MenuItem style={{ color: "#fff" }} value="W9">W-9 (U.S.)</MenuItem>*/}
                {/*        <MenuItem style={{ color: "#fff" }} value="W8BEN">W-8BEN (Non-U.S.)</MenuItem>*/}
                {/*    </TextField>*/}
                {/*</Grid>*/}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Form type"
                      value={taxType}
                      onChange={(e: React.ChangeEvent<any>) =>
                        setTaxType(e.target.value as TaxType)
                      }
                      InputProps={{ style: { color: "#fff" } }}
                      InputLabelProps={{
                        style: { color: "#fff" },
                        shrink: true,
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: { background: "#121212", color: "#fff" },
                          },
                        },
                      }}
                    >
                      <MenuItem style={{ color: "#fff" }} value="W9">
                        W-9 (U.S.)
                      </MenuItem>
                      <MenuItem style={{ color: "#fff" }} value="W8BEN">
                        W-8BEN (Non-U.S.)
                      </MenuItem>
                    </TextField>
                  </Grid>

                  {/* === NEW: Download buttons === */}
                  <Grid item xs={12}>
                    <Box display="flex" flexWrap="wrap">
                      <Button
                        variant="outlined"
                        onClick={() => openInNewTab(getBlankFormUrl(taxType))}
                        style={{
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.4)",
                        }}
                      >
                        Download blank {taxType}
                      </Button>

                      {lastTaxFile &&
                        (lastTaxFile.fileId || lastTaxFile.mediaUrl) && (
                          <Button
                            variant="outlined"
                            onClick={async () => {
                              const fileId =
                                lastTaxFile.fileId ||
                                (lastTaxFile.mediaUrl?.startsWith("/api/files/")
                                  ? lastTaxFile.mediaUrl.split("/api/files/")[1]
                                  : "");
                              if (fileId) {
                                await downloadApiFileById(
                                  fileId,
                                  lastTaxFile.filename || "my-tax-form.pdf"
                                );
                              } else if (lastTaxFile.mediaUrl) {
                                openInNewTab(lastTaxFile.mediaUrl);
                              } else {
                                showToast({
                                  message: "No file available",
                                  err: true,
                                });
                              }
                            }}
                            style={{
                              color: "#fff",
                              borderColor: "rgba(255,255,255,0.4)",
                            }}
                          >
                            Download my last submitted form
                          </Button>
                        )}
                    </Box>
                  </Grid>
                  {/* === END NEW === */}

                  <Grid item xs={12}>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={e => setTaxFile(e.target.files?.[0] || null)}
                    />
                  </Grid>
                </Grid>

                {/*<Grid item xs={12}>*/}
                {/*    <input type="file" accept=".pdf,image/*" onChange={(e) => setTaxFile(e.target.files?.[0] || null)} />*/}
                {/*</Grid>*/}
              </Grid>
            </>
          ) : (
            <>
              {/* WIZARD STEPS */}
              {wizardStep === 0 && (
                <Box>
                  <Typography style={{ marginBottom: 12 }}>
                    Step 1 — Choose form type
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label="Form type"
                    value={taxType}
                    onChange={(e: React.ChangeEvent<any>) =>
                      setTaxType(e.target.value as TaxType)
                    }
                    InputProps={{ style: { color: "#fff" } }}
                    InputLabelProps={{ style: { color: "#fff" }, shrink: true }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: { background: "#121212", color: "#fff" },
                        },
                      },
                    }}
                  >
                    <MenuItem style={{ color: "#fff" }} value="W9">
                      W-9 (U.S.)
                    </MenuItem>
                    <MenuItem style={{ color: "#fff" }} value="W8BEN">
                      W-8BEN (Non-U.S.)
                    </MenuItem>
                  </TextField>

                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={draftLoading}
                      onClick={createDraft}
                    >
                      {draftLoading ? "Starting…" : "Start"}
                    </Button>
                  </Box>
                </Box>
              )}

              {wizardStep === 1 && (
                <Box>
                  <Typography style={{ marginBottom: 12 }}>
                    Step 2 — Fill fields
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Full name"
                        value={fields.name}
                        onChange={e =>
                          setFields({ ...fields, name: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Business name (optional)"
                        value={fields.businessName}
                        onChange={e =>
                          setFields({ ...fields, businessName: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Tax classification"
                        value={fields.taxClass}
                        onChange={e =>
                          setFields({ ...fields, taxClass: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: { background: "#121212", color: "#fff" },
                            },
                          },
                        }}
                      >
                        <MenuItem style={{ color: "#fff" }} value="individual">
                          Individual / Sole prop
                        </MenuItem>
                        <MenuItem style={{ color: "#fff" }} value="company">
                          Company
                        </MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="SSN / EIN / TIN"
                        value={fields.tin}
                        onChange={e =>
                          setFields({ ...fields, tin: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Address"
                        value={fields.address}
                        onChange={e =>
                          setFields({ ...fields, address: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="City"
                        value={fields.city}
                        onChange={e =>
                          setFields({ ...fields, city: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="State/Province"
                        value={fields.state}
                        onChange={e =>
                          setFields({ ...fields, state: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="ZIP/Postal code"
                        value={fields.zip}
                        onChange={e =>
                          setFields({ ...fields, zip: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Country"
                        value={fields.country}
                        onChange={e =>
                          setFields({ ...fields, country: e.target.value })
                        }
                        InputProps={{ style: { color: "#fff" } }}
                        InputLabelProps={{
                          style: { color: "#fff" },
                          shrink: true,
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box mt={2} display="flex">
                    <Button
                      onClick={() => setWizardStep(0)}
                      style={{ color: "#fff" }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={saveFields}
                      disabled={draftLoading}
                    >
                      {draftLoading ? "Saving…" : "Save & Continue"}
                    </Button>
                  </Box>
                </Box>
              )}

              {wizardStep === 2 && (
                <Box>
                  <Typography style={{ marginBottom: 12 }}>
                    Step 3 — Signature
                  </Typography>

                  <Box mb={2}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Type your full legal name"
                      value={sigTypedName}
                      onChange={e => setSigTypedName(e.target.value)}
                      InputProps={{ style: { color: "#fff" } }}
                      InputLabelProps={{
                        style: { color: "#fff" },
                        shrink: true,
                      }}
                    />
                  </Box>

                  <Box display="flex" alignItems="center">
                    <input
                      id="sigAgree"
                      type="checkbox"
                      checked={sigAgree}
                      onChange={e => setSigAgree(e.target.checked)}
                    />
                    <label htmlFor="sigAgree" style={{ cursor: "pointer" }}>
                      I certify the information is correct and I am authorized
                      to sign.
                    </label>
                  </Box>

                  <Box mt={2} display="flex">
                    <Button
                      onClick={() => setWizardStep(1)}
                      style={{ color: "#fff" }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={saveSignature}
                      disabled={draftLoading}
                    >
                      {draftLoading ? "Saving…" : "Save & Continue"}
                    </Button>
                  </Box>
                </Box>
              )}

              {wizardStep === 3 && (
                <Box>
                  <Typography style={{ marginBottom: 12 }}>
                    Step 4 — Generate PDF
                  </Typography>
                  <Typography style={{ opacity: 0.8, marginBottom: 12 }}>
                    We’ll render your form as a PDF and attach it to the
                    submission.
                  </Typography>

                  {!!pdfUrl && (
                    <Box mb={2}>
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#60A5FA" }}
                      >
                        Open generated PDF
                      </a>
                    </Box>
                  )}

                  <Box display="flex">
                    <Button
                      onClick={() => setWizardStep(2)}
                      style={{ color: "#fff" }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={generatePdf}
                      disabled={pdfGenerating}
                      style={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.3)",
                      }}
                    >
                      {pdfGenerating ? "Generating…" : "Generate PDF"}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setWizardStep(4)}
                      disabled={!pdfUrl}
                    >
                      Continue
                    </Button>
                  </Box>
                </Box>
              )}

              {wizardStep === 4 && (
                <Box>
                  <Typography style={{ marginBottom: 12 }}>
                    Step 5 — Submit
                  </Typography>
                  <Typography style={{ opacity: 0.8, marginBottom: 12 }}>
                    Submit your form for review. You can re-submit later if
                    requested.
                  </Typography>

                  <Box display="flex">
                    <Button
                      onClick={() => setWizardStep(3)}
                      style={{ color: "#fff" }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={submitWizard}
                      disabled={submitting}
                    >
                      {submitting ? "Submitting…" : "Submit form"}
                    </Button>
                  </Box>
                </Box>
              )}
              {/* === PDF preview (завжди видно у wizard) === */}
              <Box mt={3}>
                <Typography style={{ marginBottom: 8 }}>
                  Form preview
                </Typography>

                <PdfPreview url={pdfBlobUrl || getBlankFormUrl(taxType)} />

                <Box
                  mt={1}
                  style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    style={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.3)",
                    }}
                    onClick={() =>
                      window.open(
                        getBlankFormUrl(taxType),
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    Download blank {taxType}
                  </Button>

                  {!!pdfBlobUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      style={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.3)",
                      }}
                      onClick={() =>
                        window.open(pdfBlobUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      Open generated PDF in new tab
                    </Button>
                  )}
                </Box>
              </Box>
              {/* === END PDF preview === */}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {taxTab === "upload" ? (
            <>
              <Button
                style={{ color: "#fff" }}
                onClick={() => setOpenTax(false)}
                disabled={taxUploading}
              >
                Close
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={submitTaxForm}
                disabled={taxUploading}
              >
                {taxUploading ? "Uploading..." : "Upload"}
              </Button>
            </>
          ) : (
            <Button
              style={{ color: "#fff" }}
              onClick={() => setOpenTax(false)}
              disabled={draftLoading || submitting || pdfGenerating}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutsPage;
